import { useEffect, useRef, ChangeEvent } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FormikErrors } from 'formik'

import { useFilePicker } from 'modules/Leads/LeadDetail/Windows/EmailLog/hooks'
import { useDefaultEmailFormToolbar } from './hooks/use-default-email-form-toolbar'

import { Button, Flex, Text } from 'components'
import TinyMceEditer from 'components/TinyMceEditor'
import { DragDropFileRejection } from 'components/DragAndDrop/interfaces'

import Box from '../Box'
import TextInput from '../TextInput'
import ChipComponents from '../Chip'
import ChipInput from '../ChipInput'

import { MAX_FILE_SIZE } from 'modules/Leads/LeadDetail/Windows/EmailLog/constants'

import { EmailWithRequiredValidation, getValidationMessages } from 'utils/validation'
import { excludeNoEmail } from 'modules/Leads/LeadDetail/Windows/Meeting/helper'

const { Chip } = ChipComponents

export type EmailAddresses = {
  recipients: string[]
  cc: string[]
  bcc?: string[]
}

export type EmailInfo = {
  readOnlyAddresses?: EmailAddresses
  addresses: EmailAddresses
  from: string
  subject: string
  body: string
  defaultAttachments: {
    name: string
    url: string
  }[]
  attachments: string[]
}

interface Props {
  initial: EmailInfo
  values: EmailInfo
  isUploadLoading?: boolean
  onChange?: (data: EmailInfo) => void
  onAddFile?: (acceptedFiles: Array<File>, fileRejections: DragDropFileRejection[]) => void
  errors?: FormikErrors<EmailInfo>
}

const EmailComposer = ({ initial, values, isUploadLoading, onChange, onAddFile, errors }: Props) => {
  const countRef = useRef(0)
  const { t } = useTranslation()
  const { filePicker, fileVerification } = useFilePicker({ maxSize: MAX_FILE_SIZE })

  useEffect(() => {
    countRef.current++
  }, [initial])

  const handleClickAttachment = (url: string) => {
    window.open(url)
  }

  const validateEmail = (email: string) => EmailWithRequiredValidation.isValidSync({ value: email })

  const { toolbar, emailCustomButton } = useDefaultEmailFormToolbar({
    isUploadLoading,
    filePicker: () => filePicker(onAddFile, fileVerification),
    maxFileSizeInbytes: MAX_FILE_SIZE,
  })

  return (
    <Container>
      <Header>
        <Row>
          <Label>{t('From')}</Label>
          <Flex width="100%" p="0.5rem 0">
            {errors?.from && (
              <Text color="red" isCapitalizedFirstLetter>
                {errors.from}
              </Text>
            )}
            <Chip variant="badge">{values.from}</Chip>
          </Flex>
        </Row>
        <Row>
          <Label>{t('To')}</Label>
          <Flex width="100%" p="0.5rem 0">
            {errors?.addresses?.recipients && (
              <Text color="red" isCapitalizedFirstLetter>
                {errors.addresses.recipients}
              </Text>
            )}
            <ChipInput
              readOnlyValues={values?.readOnlyAddresses?.recipients}
              values={values.addresses.recipients}
              onChange={(items) => onChange?.({ ...values, addresses: { ...values.addresses, recipients: items } })}
              validation={validateEmail}
              errorMessage={getValidationMessages().INVALID_EMAIL}
              alignItems={'flex-start'}
              height={'27px'}
              marginTop={'3px'}
            />
          </Flex>
        </Row>
        <Row>
          <Label>{t('CC')}</Label>
          <Box width="100%" p="0.5rem 0">
            <ChipInput
              readOnlyValues={excludeNoEmail(values?.readOnlyAddresses?.cc)}
              values={excludeNoEmail(values.addresses.cc)}
              onChange={(items) => onChange?.({ ...values, addresses: { ...values.addresses, cc: items } })}
              validation={validateEmail}
              errorMessage={getValidationMessages().INVALID_EMAIL}
              alignItems={'flex-start'}
              height={'27px'}
              marginTop={'3px'}
            />
          </Box>
        </Row>
        <Row>
          <Label>{t('Subject')}</Label>
          <TextInput
            width="100%"
            fontWeight="bold"
            variant="boxless"
            placeholder="email_sub_placeholder"
            value={values.subject}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange?.({ ...values, subject: e.target.value })}
          />
        </Row>
        {values.defaultAttachments?.length > 0 && (
          <Flex p="0.5rem" gap="0.5rem" flexWrap="wrap">
            {values.defaultAttachments.map((item) => (
              <Button
                key={item.name}
                icon={['far', 'paperclip']}
                variant="outline"
                maxWidth="unset"
                onClick={() => handleClickAttachment(item.url)}
              >
                {item.name}
              </Button>
            ))}
          </Flex>
        )}
      </Header>
      <EditorContainer>
        <EmailFormCommonWrapper isUploadLoading={isUploadLoading}>
          <TinyMceEditer
            reInitTriggerArrray={[initial]}
            handleChange={(htmlContent) =>
              onChange?.({
                ...values,
                body: htmlContent,
              })
            }
            init={{
              min_height: 300,
              toolbar: toolbar,
              setup: (editor) => {
                editor.on('init', () => {
                  editor.setContent(values?.body || '')
                })

                emailCustomButton(editor)
              },
            }}
          />
        </EmailFormCommonWrapper>
      </EditorContainer>
    </Container>
  )
}

export const EmailFormCommonWrapper = styled.div<{ isUploadLoading?: boolean }>`
  [data-mce-name='emailattachmentbutton'] {
    ${(p) => (p.isUploadLoading ? `pointer-events: none;` : '')}
  }
`
const Container = styled.div`
  .DraftEditor-editorContainer {
    z-index: 0;
  }
`

const Header = styled.div`
  margin-top: 0.5rem;

  > * {
    border-bottom: 1px solid ${(p) => p.theme.colors.defaultBorder};
  }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  padding: 0 10px;
`

const Label = styled.label`
  font-size: 14px;
  width: 50px;
`

const EditorContainer = styled.div`
  margin: 0.5rem;
`

export default EmailComposer
